<template>
    <div class="my-home flow__panel flow__panel--positioned-buttons">
        <h2 class="my-home__title --desktop-only">
            {{ property ? property.asString() : 'Your property' }}
        </h2>
        <h2 class="my-home__intermediate-title --not-desktop">
            {{ property ? property.asShortString() : 'Your property' }}
        </h2>
        <div v-if="isServedByScheme"
             class="my-home__details flow__container-fluid">
            <div>
                <div class="my-home__details__intro">
                    <p v-if="isGuest">
                        You need to be logged in to edit the property data. You can <router-link class="my-home__details__intro__log-in-link" :to="{ name: 'LogIn' }">
                            log in
                        </router-link> or <router-link class="my-home__details__intro__register-link" :to="{ name: 'Register' }">
                            create a new account
                        </router-link>.
                    </p>
                    <error-handler class="my-home__details__errors" />
                </div>
                <div class="my-home__details__content">
                    <summary-panel class="my-home__details__summary" />
                    <p class="my-home__details__attributions-text">
                        <a class="my-home__details__attributions-link" @click="onAttributionsClick">{{ allResources.components.appCore.attributionsPanel.linkText }}</a>
                    </p>
                    <div class="my-home__details__cta">
                        <cta-tile class="my-home__details__cta__tile cta-tile--branded cta-tile--grid"
                                  name="build"
                                  :button-text="ctaButtonText"
                                  show-small-image
                                  secondary-button-text="Edit"
                                  icon-class="fas fa-chevron-circle-right"
                                  icon-location="post"
                                  type="cta"
                                  breakout-extended
                                  @click="onProceed"
                                  @secondary-click="onEdit" />
                    </div>
                </div>
            </div>
            <div class="my-home__details__sidebar">
                <attributions-panel />
            </div>
        </div>
        <hh-error-display v-else-if="errorMessage"
                          :error="errorMessage" />
        <no-service-scheme-msg v-else class="my-home__details" />
    </div>
</template>

<script>

import Vuex from 'vuex'

import ProgressManager from '@/progress-manager'
import PropertyManager from '@/property-manager'
import PlanBuilderManager from '@/plan-builder-manager'

import Property from '@/models/property'

import AnalyticsPage from './analytics-page'
import AttributionsHelper from '@/components/attributions-helper'
import AttributionsPanel from '@/components/app-core/attributions-panel'
import ResourcesHelper from '@/components/resources-helper'
import MyHomeDataErrorsDisplay from './my-home-data-errors-display'

import CtaTile from '@/components/app-core/cta-tile'
import ErrorHandler from '@/components/app-core/error-handler'
import NoServiceSchemeMsg from '@/components/app-core/no-service-scheme-msg'
import SummaryPanel from '@/components/my-home/summary-panel'

export default {

    mixins: [
        AnalyticsPage,
        AttributionsHelper,
        MyHomeDataErrorsDisplay,
        ResourcesHelper,
    ],

    data() {
        return {
            /**
             * @return {Property|null}
             */
            property: null,

            /**
             * @type {?string}
             */
            errorMessage: null,
        }
    },

    computed: {

        ...Vuex.mapGetters([
            'userSignedIn',
        ]),

        /**
         * @returns {string}
         */
        ctaButtonText() {
            const overrideText = this.myResources.ctaBuildPlan || null
            return overrideText
                ? overrideText
                : `Build your ${ this.$scheme.shortTitle } plan`
        },

        /**
         * @returns {boolean}
         */
        isGuest() {
            return !this.isSignedIn
        },

        /**
         * @returns {boolean}
         */
        isSignedIn() {
            return Boolean(this.userSignedIn)
        },

        /**
         * @returns {boolean}
         */
        isServedByScheme() {
            return this.property && this.property.isServedByScheme() || false
        },

        /**
         * Return the resources object for this page.
         * @return {MyHomeResources}
         */
        myResources() {
            return this.parseResources('views.myHome')
        },

        /**
         * @returns {{id: number}|{}}
         */
        query() {
            return this.property
                ? { id: this.property.id }
                : this.$route.query ? { id: this.propertyId } : {}
        },

        /**
         * @returns {number}
         */
        propertyId() {
            return parseInt(this.$route.query.id !== null ? this.$route.query.id : 0)
        },
    },

    /**
     * View mounted event.
     */
    async mounted() {
        this.trackEvent('home_info', 'home_info_loaded')

        await this._loadHomeDataAsync()

        if (!this.isServedByScheme)
            this.trackEvent('not_serviced_search', this.property ? this.property.postcode : 'unknown_property')
    },

    methods: {
        /**
         * @returns {Promise<void>}
         * @private
         */
        async _loadHomeDataAsync() {
            const property = await PropertyManager.switchChosenPropertyAsync(this.propertyId)

            this.$logger.logObject(property, `Property loaded with ID ${ this.propertyId }`, 'MyHome')

            if (property === null) {
                this.errorMessage = `Unable to load property with ID: ${ this.propertyId }`
                return Promise.resolve()
            }

            this.property = Property.ensure(property)

            if (this.isServedByScheme)
                await PropertyManager.loadHomeDataIfNeededOrForcedAsync(this.property.id, true)
        },

        /**
         * Event.
         */
        async onProceed() {
            if (this.scheme.hasFixedBudget)
                await PlanBuilderManager.modifyBudgetAsync(this.$scheme.fixedBudget)

            if (this.scheme.hasFixedMotivation)
                await PlanBuilderManager.setMotivationAsync(this.$scheme.fixedMotivation)

            this.$router.push(ProgressManager.getNext(this.$route.name))
        },

        /**
         * Event.
         */
        onEdit() {
            this.$router.push({ name: 'MyHomeEdit', query: this.query })
        },
    },

    components: {
        AttributionsPanel,
        CtaTile,
        ErrorHandler,
        NoServiceSchemeMsg,
        SummaryPanel,
    },
}

</script>

<style lang="scss">

// Ensure colours and other visual style elements are added to theme files, not placed in here.
// src\scss\schemes\views\_my-home.scss

.my-home {
    @include element('details') {
        position: relative;
        @include my-home-details-shared;

        @include bp-min-small {
            margin-bottom: rem(10px);
        }

        @include bp-min-mid {
            margin-bottom: 0;
        }

        @include element('attributions-text') {
            margin-top: $wide-gutter;
            margin-left: 60px;

            @include bp-min-small {
                display: none;
            }
        }

        @include element('sidebar') {
            display: none;

            @include bp-min-small {
                display: block;
                padding-top: 1rem;
            }

            @include bp-min-wide {
                padding-top: 0;
                padding-left: rem(50px);
            }
        }

        @include element('intro') {

            > * {
                font-size: 0.9rem;

                @include bp-min-wide {
                    font-size: 1rem;
                }
            }
        }

        @include element('summary') {
            @include bp-min-mid {
                margin-right: $mid-gutter;
            }

            @include bp-min-wide {
                margin-right: $wide-gutter;
            }
        }

        @include nested-element('cta', 'tile') {
            .cta-tile__buttons {
                @include bp-min-mid {
                    margin-left: 60px;
                }
            }
        }

        @include element('content') {
            @include bp-min-mid {
                display: block;
            }

            @include bp-min-wide {
                display: grid;
            }
        }
    }
}

</style>
